'use client';

import * as React from 'react';

import { Badge } from '@workspace/ui/components/badge';
import { Label } from '@workspace/ui/components/label';
import {
  RadioGroup,
  RadioGroupItem
} from '@workspace/ui/components/radio-group';
import { cn } from '@workspace/ui/lib/utils';

import { billingConfigDisplayIntervals } from '../config';
import { getMaxYearlySavingsPercent } from '../helpers';
import { PriceInterval } from '../schema';

export type PriceIntervalSelectorProps =
  React.HtmlHTMLAttributes<HTMLDivElement> & {
    interval: PriceInterval;
    onIntervalChange: (interval: PriceInterval) => void;
    /** Intervals to offer; defaults to the full billing-config display list. */
    intervals?: PriceInterval[];
  };

const INTERVAL_LABELS: Partial<Record<PriceInterval, string>> = {
  [PriceInterval.Month]: 'Monthly',
  [PriceInterval.Year]: 'Annual',
  [PriceInterval.OneTime]: 'One-Time',
  [PriceInterval.AddOns]: 'Add-Ons'
};

export function PriceIntervalSelector({
  interval,
  onIntervalChange,
  intervals = billingConfigDisplayIntervals,
  className,
  ...other
}: PriceIntervalSelectorProps): React.JSX.Element {
  const id = React.useId();
  const hasYearly = intervals.includes(PriceInterval.Year);
  const savingsPercent = hasYearly ? getMaxYearlySavingsPercent() : 0;

  return (
    // pt-8 reserves room inside this component's own border box for the
    // savings badge floating above the Annual chip, so no ancestor
    // overflow-hidden can clip it and it causes no layout shift.
    <div
      className={cn('pt-8', className)}
      {...other}
    >
      <RadioGroup
        value={interval}
        aria-label="Billing interval"
        onValueChange={(value) => onIntervalChange(value as PriceInterval)}
        // flex-wrap is a safety net below ~340px viewports; gap-y-8 keeps a
        // wrapped row clear of the badge floating above the chip.
        className="flex flex-row flex-wrap items-center justify-center gap-x-2 gap-y-8 sm:gap-x-3"
      >
        {intervals.map((displayInterval) => {
          const isActive = interval === displayInterval;
          const showSavings =
            displayInterval === PriceInterval.Year && savingsPercent > 0;
          const itemId = `${id}-${displayInterval}`;
          return (
            <div key={displayInterval}>
              {/* Real radio for a11y: roving tabindex + arrow keys via
                  Radix. Visually hidden; the Label below is the chip. */}
              <RadioGroupItem
                value={displayInterval}
                id={itemId}
                className="peer sr-only"
              />
              <Label
                htmlFor={itemId}
                className={cn(
                  'relative flex h-11 cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-xl border bg-card px-3 text-sm font-medium transition-colors duration-200 sm:gap-2.5 sm:px-5',
                  // Keyboard focus ring (outline, so it never fights the
                  // active state's ring-1) driven by the hidden radio.
                  'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring',
                  isActive
                    ? 'border-primary text-primary shadow-sm ring-1 ring-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                )}
              >
                {/* Radio-dot indicator (state is conveyed by the radio
                    itself, so this is decorative). */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-3.5 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 sm:size-4',
                    isActive ? 'border-primary' : 'border-border'
                  )}
                >
                  <span
                    className={cn(
                      'size-1.5 rounded-full bg-primary transition-transform duration-200 motion-reduce:transition-none sm:size-2',
                      isActive ? 'scale-100' : 'scale-0'
                    )}
                  />
                </span>
                {INTERVAL_LABELS[displayInterval] ?? displayInterval}
                {/* Floating savings badge, visually attached to the Annual
                    chip's top edge. Inside the Label so clicking it selects
                    Annual and screen readers announce it as part of the
                    option. Default Badge variant = the same filled-primary
                    badge as the recommended card's "Recommended". */}
                {showSavings && (
                  <Badge className="absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap">
                    Save up to {savingsPercent}%
                  </Badge>
                )}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}
